/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  entityFieldsMerger,
  entityReplaceOrMergeWith,
  onChangeReducer,
  removeDirtyRow,
  setDirty,
  setEntityField,
  setEntityFields,
  setNewDirtyType,
  setUpdatedDirtyType,
  toOnChangeReducer,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { getJeOrderId, JeCostCenter, JeOrder } from '../../../resources/job-estimate-details.model';
import {
  JeDetailsState,
  JeDetailsStateEvent,
  JeDetailsStateEventType,
  jeOrderTable,
  queryJeAuthorized,
  queryJeOrder,
} from '../je-details.state';
import { updateCostCenters } from './je-details-cc.reducers';
import { selectionConsistencyReducer } from './je-details-selection.reducers';

export const mergeJeOrder = entityReplaceOrMergeWith(entityFieldsMerger<JeOrder>(['amount', 'attachments']));

export const jeDetailsOrderReducer = onChangeReducer(
  ordersReducer,
  costCenterOrderOnChangeReducer,
  rootItemOnChangeReducer,
  toOnChangeReducer(selectionConsistencyReducer),
);

const updateCcDirtyType = (data: Maybe<JeCostCenter>) => data ? setUpdatedDirtyType(data) : void 0;
const getOrderCostCenterId = (state: JeDetailsState, orderId: ID) =>
  queryJeOrder(state, orderId)!.costCenterId; // tslint:disable-line:no-non-null-assertion
const setOrderFields = (source: JeOrder, fields: JeOrder, isAuthorized: boolean) => {
  const newSource = setEntityFields(source, fields);
  // if order is authorized we save order immediately and do not mark it as dirty
  return newSource !== source && !isAuthorized ? setUpdatedDirtyType(newSource) : newSource;
};

function ordersReducer(state: JeDetailsState, event: JeDetailsStateEvent): JeDetailsState {
  switch (event.type) {
    case JeDetailsStateEventType.OrderAdd:
      return setEntityField(
        state,
        'jobOrders',
        jeOrderTable.insert(state.jobOrders, setNewDirtyType(event.order)));
    case JeDetailsStateEventType.OrderUpdate:
      return setEntityField(
        state,
        'jobOrders',
        jeOrderTable.mergeById(
          state.jobOrders,
          getJeOrderId(event.order),
          (row: JeOrder) => setOrderFields(row, event.order, queryJeAuthorized(state))));
    case JeDetailsStateEventType.OrderRemove:
      return setEntityField(
        state,
        'jobOrders',
        jeOrderTable.mergeById(state.jobOrders, event.orderId, removeDirtyRow));
    default:
      return state;
  }
}

function costCenterOrderOnChangeReducer(oldState: JeDetailsState,
                                        newState: JeDetailsState,
                                        event: JeDetailsStateEvent): JeDetailsState {
  switch (event.type) {
    case JeDetailsStateEventType.OrderAdd:
    case JeDetailsStateEventType.OrderUpdate:
      // mark as dirty only when page is not authorized
      return queryJeAuthorized(newState)
        ? newState
        : updateCostCenters(newState, [event.order.costCenterId], updateCcDirtyType);
    case JeDetailsStateEventType.OrderRemove:
      return updateCostCenters(
        newState,
        [getOrderCostCenterId(oldState, event.orderId)],
        updateCcDirtyType);
    default:
      return newState;
  }
}

function rootItemOnChangeReducer(_: JeDetailsState,
                                 state: JeDetailsState,
                                 event: JeDetailsStateEvent): JeDetailsState {
  switch (event.type) {
    case JeDetailsStateEventType.OrderAdd:
    case JeDetailsStateEventType.OrderUpdate:
    case JeDetailsStateEventType.OrderRemove:
      // mark as dirty only when page is not authorized
      return queryJeAuthorized(state) ? state : setDirty(state);
    default:
      return state;
  }
}
